import { useMemo } from "react";
import type { TreeNodeAdmin } from "@/types/admin";

interface TreeNodeVis {
  entry_id: string;
  parent_entry_id: string | null;
  owner_user_id: string;
  depth: number;
  status: string;
  children: TreeNodeVis[];
  x?: number; y?: number;
}

const NODE_W = 140;
const NODE_H = 60;
const LEVEL_H = 100;
const NODE_GAP = 20;

export function TreeVisualization({
  nodes, onSelect,
}: { nodes: TreeNodeAdmin[]; onSelect?: (entry_id: string) => void }) {
  const { tree, width, height } = useMemo(() => {
    // Build hierarchy
    const byId = new Map<string, TreeNodeVis>();
    for (const n of nodes) {
      byId.set(n.entry_id, {
        entry_id: n.entry_id,
        parent_entry_id: n.parent_entry_id,
        owner_user_id: n.owner_user_id,
        depth: n.depth,
        status: n.status,
        children: [],
      });
    }
    let root: TreeNodeVis | null = null;
    for (const n of byId.values()) {
      if (n.parent_entry_id && byId.has(n.parent_entry_id)) {
        byId.get(n.parent_entry_id)!.children.push(n);
      } else if (!n.parent_entry_id) {
        root = n;
      }
    }

    // Compute layout (simple recursive)
    let maxX = 0, maxDepth = 0;
    let cursor = 0;
    const layout = (node: TreeNodeVis, depth: number) => {
      maxDepth = Math.max(maxDepth, depth);
      if (node.children.length === 0) {
        node.x = cursor * (NODE_W + NODE_GAP);
        node.y = depth * LEVEL_H;
        cursor++;
        maxX = Math.max(maxX, node.x);
        return;
      }
      for (const c of node.children) layout(c, depth + 1);
      const first = node.children[0].x!;
      const last = node.children[node.children.length - 1].x!;
      node.x = (first + last) / 2;
      node.y = depth * LEVEL_H;
    };
    if (root) layout(root, 0);

    return {
      tree: root,
      width: (maxX + NODE_W + 40),
      height: (maxDepth + 1) * LEVEL_H + 60,
    };
  }, [nodes]);

  if (!tree) {
    return (
      <div className="p-8 text-center text-sm text-surface-500">
        No tree nodes yet. Waiting for first placement.
      </div>
    );
  }

  const nodeList: TreeNodeVis[] = [];
  const flatten = (n: TreeNodeVis) => {
    nodeList.push(n);
    n.children.forEach(flatten);
  };
  flatten(tree);

  return (
    <div className="overflow-auto max-w-full">
      <svg width={width} height={height} className="min-w-full">
        {/* edges */}
        {nodeList.flatMap((n) =>
          n.children.map((c) => (
            <line
              key={`${n.entry_id}-${c.entry_id}`}
              x1={n.x! + NODE_W / 2}
              y1={n.y! + NODE_H}
              x2={c.x! + NODE_W / 2}
              y2={c.y!}
              stroke="#cbd5e1"
              strokeWidth={2}
            />
          )),
        )}
        {/* nodes */}
        {nodeList.map((n) => (
          <g key={n.entry_id}
             transform={`translate(${n.x}, ${n.y})`}
             className="cursor-pointer"
             onClick={() => onSelect?.(n.entry_id)}>
            <rect
              width={NODE_W} height={NODE_H} rx={10}
              fill={
                n.status === "completed" ? "#dcfce7"
              : n.status === "processing" ? "#dbeafe"
              : n.status === "blocked"    ? "#fee2e2"
              : "#f1f5f9"
              }
              stroke={
                n.status === "completed" ? "#22c55e"
              : n.status === "processing" ? "#3b82f6"
              : n.status === "blocked"    ? "#ef4444"
              : "#cbd5e1"
              }
              strokeWidth={1.5}
              className="hover:brightness-95"
            />
            <text x={NODE_W / 2} y={22} textAnchor="middle"
                  className="text-xs font-bold fill-surface-900 font-mono">
              {n.entry_id}
            </text>
            <text x={NODE_W / 2} y={38} textAnchor="middle"
                  className="text-2xs fill-surface-600">
              {n.owner_user_id}
            </text>
            <text x={NODE_W / 2} y={52} textAnchor="middle"
                  className="text-2xs fill-surface-500 capitalize">
              L{n.depth} · {n.status}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
}
